import type { RenderComponentProps } from "replo-runtime/shared/types";
import type { Component } from "schemas/component";

import React from "react";

import { useBundleBuilder } from "../BundleBuilderContext";

const getCustomProps = (props: Component["props"]) => {
  const { progressLabel, tieredProgressLabel } = props;
  return {
    progressLabel: progressLabel as "string" | null,
    tieredProgressLabel: tieredProgressLabel as "string" | null,
  };
};
export const BundleProgressBar: React.FC<RenderComponentProps> = ({
  componentAttributes,
  component,
}) => {
  const { progressLabel, tieredProgressLabel } = getCustomProps(
    component.props,
  );
  const {
    getBundleTotalQuantity,
    isTiered,
    formatPrice,
    bundleDiscountThresholds,
    nextBundleDiscountThreshold,
    selectedBundleDiscountThreshold,
  } = useBundleBuilder();
  const maxQuantity = bundleDiscountThresholds.slice(-1)[0]!.quantity;
  const targetBundleDiscountThreshold =
    nextBundleDiscountThreshold ?? selectedBundleDiscountThreshold;
  const backgroundColor = component.props?.style?.color ?? "#1E293B";
  const total = getBundleTotalQuantity();
  const target = isTiered
    ? maxQuantity
    : selectedBundleDiscountThreshold.quantity;
  const progress = Math.min(100, (total / target) * 100);
  const { value, type } = targetBundleDiscountThreshold.discount;
  const formattedDiscountValue =
    type === "percentageOff" ? `${value}%` : `${formatPrice(String(value))}`;
  const itemsLeft = targetBundleDiscountThreshold.quantity - total;
  const tieredLabel = `${tieredProgressLabel ?? "Add more items to get "} ${formattedDiscountValue}`;
  const message = isTiered
    ? tieredLabel
    : `${total}/${target} ${progressLabel ?? "Items Added"}`;
  return (
    <div {...componentAttributes}>
      {itemsLeft > 0 && (
        <h3
          style={{
            margin: "0 0 16px 0",
          }}
        >
          {message}
        </h3>
      )}
      <div style={{ position: "relative" }}>
        <div
          style={{
            width: "100%",
            height: "10px",
            backgroundColor: "#e1e1e1",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "block",
              width: `${progress}%`,
              height: "100%",
              backgroundColor: backgroundColor,
              transition: "width 0.3s ease",
            }}
          />
          {isTiered &&
            bundleDiscountThresholds.map(({ quantity }) => {
              return (
                <div
                  key={quantity}
                  style={{
                    display: "block",
                    position: "absolute",
                    top: "-5px",
                    left: `calc(${(quantity / maxQuantity) * 100}% - 2px)`,
                    width: "1px",
                    height: "20px",
                    border: "solid 1px currentColor",
                  }}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};
